import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import {
  useBulkUploadStaffInvitationsMutation,
  useLazyDownloadStaffInvitationTemplateQuery,
} from "../redux/services/inviteApi";
import * as XLSX from "xlsx";
import { usePlanLimit } from "../hooks/usePlanLimit";
import { getApiErrorMessage } from "../utils/apiError";

export const useBulkUploadStaffController = ({
  setIsModalOpen,
}: {
  setIsModalOpen: (param: boolean) => void;
}) => {
  const [triggerDownload, { isFetching: isFetchingTemplate }] =
    useLazyDownloadStaffInvitationTemplateQuery();
  const [bulkUploadInvitations, { isLoading }] =
    useBulkUploadStaffInvitationsMutation();
  const { handlePlanLimitError, showBulkUploadResult } = usePlanLimit();

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [requirementModalOpen, setRequirementModalOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);

  const allowedExtensions = React.useMemo(() => [".csv", ".xlsx"], []);

  const isValidFile = useCallback(
    (file: File) => {
      const fileName = file.name.toLowerCase();
      return allowedExtensions.some((ext) => fileName.endsWith(ext));
    },
    [allowedExtensions]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];

    if (!uploadedFile) return;

    if (!isValidFile(uploadedFile)) {
      toast.error("Only .csv and .xlsx files are supported.");
      return;
    }

    setFile(uploadedFile);

    parseFile(uploadedFile)
      .then((parsedData) => {
        setPreviewData(parsedData);
      })
      .catch(() => {
        toast.error("Failed to parse file.");
      });
  };

  const parseFile = (uploadedFile: File): Promise<any[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          resolve(json as any[][]);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(uploadedFile);
    });
  };

  const handleFileDrop = useCallback(
    (droppedFile: File) => {
      if (!isValidFile(droppedFile)) {
        toast.error("Only .csv and .xlsx files are supported.");
        return;
      }

      setFile(droppedFile);

      parseFile(droppedFile)
        .then((parsedData) => {
          setPreviewData(parsedData);
        })
        .catch(() => {
          toast.error("Failed to parse file.");
        });
    },
    [isValidFile]
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileDrop(e.dataTransfer.files[0]);
        e.dataTransfer.clearData();
      }
    },
    [handleFileDrop]
  );

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await bulkUploadInvitations(formData).unwrap();

      // The API may say some people were skipped because of the plan limit.
      showBulkUploadResult(res?.data?.message ?? res?.message, "Users bulk upload successful!");

      setIsModalOpen(false);  // closes the modal
      setPreviewModal(false); // closes preview if open

      // ✅ reset state for next upload
      setFile(null);
      setPreviewData([]);
    } catch (err) {
      console.error(err);
      if (handlePlanLimitError(err)) return;
      toast.error(getApiErrorMessage(err, "Upload failed. Please try again."));
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const blob = await triggerDownload().unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "invitation-template.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download template.");
    }
  };

  return {
    // state
    file,
    isLoading,
    isDragging,
    isFetchingTemplate,
    previewData,
    requirementModalOpen,
    previewModal,

    // function
    handleFileChange,
    handleFileDrop,
    onDragOver,
    onDragLeave,
    onDrop,
    handleSubmit,
    handleTemplateDownload,
    setFile,
    setRequirementModalOpen,
    setPreviewModal,
  };
};

